import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { TIPI_SOGGETTO_OPTIONS } from '../../constants/tipi-soggetto.constants';
import { Indirizzo } from '../../models/indirizzo.model';

@Component({
  selector: 'app-soggetto-editor',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    DropdownModule,
    ReactiveFormsModule,
    ToastModule
  ],
  template: `
    <p-table
      #table
      [value]="items$ | async"
      [paginator]="true"
      [rows]="10"
      [alwaysShowPaginator]="false"
      selectionMode="multiple"
      [(selection)]="selectedIndirizzi"
      dataKey="indirizzoId">
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 12rem;">Tipo</th>
          <th>{{ itemTitle }}</th>
          <th class="col3buttons" rowspan="2">
            <button
              class="addButton p-button-info addButtonSpacing"
              pButton
              type="button"
              icon="fas fa-sync"
              title="Raggruppa"
              (click)="openSyncIndirizzi()"
              [disabled]="selectedIndirizzi.length < 2 || !canModified"></button>
            <button
              *ngIf="showAddButton"
              class="addButton addButtonSpacing"
              pButton
              type="button"
              icon="fas fa-plus"
              title="Nuovo Soggetto"
              (click)="onAddClick()"
              [disabled]="!canModified"></button>
          </th>
        </tr>
        <tr>
          <th>
            <p-dropdown
              [options]="tipiSoggetto"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              placeholder="Tutti"
              style="width: 100%;"
              (onChange)="onTipoFilterChange($event.value, table)">
            </p-dropdown>
          </th>
          <th>
            <input
              pInputText
              type="text"
              placeholder="Filtra..."
              style="width:100%"
              (input)="table.filter($any($event.target).value, nameProperty, 'contains')">
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-item>
        <tr [pSelectableRow]="item">
          <td>{{ getTipoLabel(item.tipoSoggettoId) }}</td>
          <td>{{ getNomeDisplay(item) }}</td>
          <td class="p-d-flex">
            <button
              pButton
              type="button"
              icon="fas fa-pen"
              class="p-button-warning"
              style="margin-right: 5px;"
              title="Modifica"
              (click)="openModal('edit', item)"
              [disabled]="!canModified"></button>
            <button
              pButton
              type="button"
              icon="fas fa-times"
              class="p-button-danger"
              title="Elimina"
              (click)="removeItem(item)"
              [disabled]="!canModified"></button>
            <button
              pButton
              type="button"
              [icon]="item.bloccato ? 'pi pi-lock' : 'pi pi-unlock'"
              style="margin-left: 5px;"
              [class.p-button-danger]="item.bloccato"
              [class.p-button-success]="!item.bloccato"
              [title]="item.bloccato ? 'Sblocca' : 'Blocca'"
              (click)="bloccaIndirizzo(item)"
              [disabled]="!canModified"></button>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="3">Nessun elemento</td>
        </tr>
      </ng-template>
    </p-table>

    <p-dialog
      [(visible)]="isDialogOpen"
      [modal]="true"
      [closable]="false"
      [closeOnEscape]="false"
      [style]="{ width: '48rem' }"
      [contentStyle]="{ 'min-height': '380px' }">
      <p-header>
        <span *ngIf="editMode === 'new'">Aggiungi {{ itemTitle }}</span>
        <span *ngIf="editMode === 'edit'">Modifica {{ itemTitle }}</span>
        <span *ngIf="editMode === 'sync'">Raggruppa {{ itemTitle }}</span>
      </p-header>

      <form [formGroup]="form" *ngIf="editMode !== 'sync'" class="p-fluid">
        <div class="field">
          <label for="tipoSoggetto">Tipo*</label>
          <p-dropdown
            id="tipoSoggetto"
            formControlName="tipoSoggettoId"
            [options]="tipiSoggetto"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            placeholder="Seleziona..."></p-dropdown>
        </div>
        <div class="field" *ngIf="tipoSoggetto">
          <label for="name">{{ nomeLabel }}*</label>
          <input id="name" type="text" pInputText formControlName="name" />
        </div>
        <div class="field" *ngIf="isPersonaFisica">
          <label for="cognome">Cognome*</label>
          <input id="cognome" type="text" pInputText formControlName="cognome" />
        </div>
        <div class="field" *ngIf="isPersonaFisica || isPersonaGiuridica">
          <label for="codiceFiscale">
            {{ isPersonaGiuridica ? 'Codice Fiscale / Partita IVA' : 'Codice fiscale' }}
          </label>
          <input id="codiceFiscale" type="text" pInputText formControlName="codiceFiscale" />
        </div>
        <div class="field" *ngIf="isPai">
          <label for="denominazioneAoo">Denominazione Amministrazione AOO / Codice IPA AOO*</label>
          <input id="denominazioneAoo" type="text" pInputText formControlName="denominazioneAoo" />
        </div>
        <div class="field" *ngIf="isPae">
          <label for="indirizziDigitaliRiferimento">Indirizzi digitali di riferimento*</label>
          <input id="indirizziDigitaliRiferimento" type="text" pInputText formControlName="indirizziDigitaliRiferimento" />
        </div>
      </form>

      <div *ngIf="editMode === 'sync'" class="min-content">
        <p-table
          class="noSize"
          [value]="selectedIndirizzi"
          [(selection)]="selectIndirizzo"
          dataKey="indirizzoId"
          selectionMode="single"
          (onRowSelect)="setFormSelect()">
          <ng-template pTemplate="header">
            <tr>
              <th style="width: 3rem"></th>
              <th style="width: 25rem">Nome</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-indirizzo>
            <tr>
              <td>
                <p-tableRadioButton [value]="indirizzo"></p-tableRadioButton>
              </td>
              <td>{{ indirizzo.nomeIndirizzo }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="summary">
            <div style="text-align: left">
              Raggruppa in:
              {{ selectIndirizzo ? selectIndirizzo.nomeIndirizzo : 'nessun indirizzo selezionato' }}
            </div>
          </ng-template>
        </p-table>
      </div>

      <p-footer>
        <button pButton type="button" label="Annulla" class="p-button-secondary" (click)="resetForm()"></button>
        <button
          pButton
          type="button"
          [label]="dialogPrimaryLabel"
          (click)="saveItem()"
          [disabled]="isPrimaryDisabled"></button>
      </p-footer>
    </p-dialog>

    <p-toast key="toast" position="top-center"></p-toast>
  `,
  styles: [
    `
      .col3buttons {
        width: 130px;
      }

      .min-content {
        width: min-content;
      }

      .addButtonSpacing {
        margin-right: 0.5rem;
      }
    `
  ]
})
export class SoggettoEditorComponent implements OnInit {
  @Input() public itemTitle = 'Soggetto';
  @Input() public nameProperty: keyof Indirizzo & string = 'nomeIndirizzo';
  @Input() public filterRows?: (items: Indirizzo[]) => Indirizzo[];
  @Input() public canModified = true;
  @Input() public showAddButton = true;
  @Output() public add = new EventEmitter<void>();

  public selectedIndirizzi: Indirizzo[] = [];
  public selectIndirizzo: Indirizzo | null = null;
  public editMode: 'new' | 'edit' | 'sync' = 'new';
  public form: FormGroup;
  public items$!: Observable<Indirizzo[]>;
  public itemInEdit: Indirizzo | null = null;
  public isDialogOpen = false;
  public tipiSoggetto = TIPI_SOGGETTO_OPTIONS;
  private readonly tipoLabelMap = new Map<number, string>(
    TIPI_SOGGETTO_OPTIONS.filter(option => option.value != null).map(option => [option.value as number, option.label])
  );

  constructor(
    private readonly indirizziService: AnagraficaIndirizziService,
    private readonly fb: FormBuilder,
    private readonly messageService: MessageService
  ) {
    this.form = this.fb.group({
      tipoSoggettoId: [null, Validators.required],
      name: [null, Validators.required],
      cognome: [''],
      codiceFiscale: [''],
      denominazioneAoo: [''],
      indirizziDigitaliRiferimento: ['']
    });

    this.form.get('tipoSoggettoId')?.valueChanges.subscribe(() => this.applyTipoSoggettoRules());
  }

  ngOnInit(): void {
    this.items$ = this.indirizziService.getAll().pipe(
      map(items => (this.filterRows ? this.filterRows(items) : items))
    );
    this.applyTipoSoggettoRules();
  }

  get dialogPrimaryLabel(): string {
    if (this.editMode === 'sync') {
      return 'Raggruppa';
    }

    return this.editMode === 'new' ? 'Aggiungi' : 'Modifica';
  }

  get isPrimaryDisabled(): boolean {
    if (this.editMode === 'sync') {
      return !this.selectIndirizzo;
    }

    return this.form.invalid;
  }

  public onAddClick(): void {
    if (this.add.observers.length > 0) {
      this.add.emit();
    } else {
      this.openModal('new');
    }
  }

  get tipoSoggetto(): number | null {
    return this.form.get('tipoSoggettoId')?.value ?? null;
  }

  get isPersonaFisica(): boolean {
    return this.tipoSoggetto === 1;
  }

  get isPersonaGiuridica(): boolean {
    return this.tipoSoggetto === 2;
  }

  get isPai(): boolean {
    return this.tipoSoggetto === 3;
  }

  get isPae(): boolean {
    return this.tipoSoggetto === 4;
  }

  get nomeLabel(): string {
    switch (this.tipoSoggetto) {
      case 2:
        return 'Denominazione Organizzazione';
      case 3:
        return 'Denominazione Amministrazione / Codice IPA';
      case 4:
        return 'Denominazione Amministrazione';
      default:
        return 'Nome';
    }
  }

  public openModal(mode: 'new' | 'edit', item?: Indirizzo): void {
    this.isDialogOpen = true;
    this.editMode = mode;
    this.itemInEdit = item ?? null;
    this.form.patchValue({
      tipoSoggettoId: item?.tipoSoggettoId ?? null,
      name: item?.[this.nameProperty] ?? null,
      cognome: item?.cognome ?? null,
      codiceFiscale: item?.codiceFiscale ?? null,
      denominazioneAoo: item?.denominazioneAoo ?? null,
      indirizziDigitaliRiferimento: item?.indirizziDigitaliRiferimento ?? null
    }, { emitEvent: false });
    this.applyTipoSoggettoRules(false);

    setTimeout(() => {
      document.getElementById('name')?.focus();
    }, 50);
  }

  public openSyncIndirizzi(): void {
    if (this.selectedIndirizzi.length < 2) {
      return;
    }
    this.isDialogOpen = true;
    this.editMode = 'sync';
    this.selectIndirizzo = null;
  }

  public setFormSelect(): void {
    if (this.selectIndirizzo) {
      this.form.controls['name'].setValue(this.selectIndirizzo[this.nameProperty]);
    }
  }

  public resetForm(): void {
    this.isDialogOpen = false;
    this.editMode = 'new';
    this.itemInEdit = null;
    this.selectIndirizzo = null;
    this.form.reset();
    this.applyTipoSoggettoRules();
  }

  public saveItem(): void {
    if (this.editMode === 'sync') {
      this.raggruppaIndirizzi();
      return;
    }

    if (this.editMode === 'new') {
      const payload = this.buildPayload();
      this.indirizziService.create(payload).subscribe(
        () => this.handleSuccess('Creazione'),
        err => this.handleError(err)
      );
    } else if (this.itemInEdit) {
      const updated = this.buildPayload(this.itemInEdit);
      this.indirizziService.update(updated).subscribe(
        () => this.handleSuccess('Modifica'),
        err => this.handleError(err)
      );
    }
  }

  public removeItem(item: Indirizzo): void {
    this.indirizziService.delete(item).subscribe(
      () => this.handleSuccess('Eliminazione'),
      err => this.handleError(err)
    );
  }

  public bloccaIndirizzo(item: Indirizzo): void {
    item.bloccato = !item.bloccato;
    this.indirizziService.update(item).subscribe(
      () => this.handleSuccess('Modifica'),
      err => this.handleError(err)
    );
  }

  private raggruppaIndirizzi(): void {
    if (!this.selectIndirizzo) {
      return;
    }
    const array = [...this.selectedIndirizzi];
    const index = array.findIndex(el => el.indirizzoId === this.selectIndirizzo?.indirizzoId);
    if (index >= 0) {
      array.splice(index, 1);
    }

    let processed = 0;
    const total = array.length;

    if (total === 0) {
      this.resetForm();
      return;
    }

    array.forEach(el => {
      this.indirizziService.cambiaindirizzo(el.indirizzoId, this.selectIndirizzo!.indirizzoId).subscribe({
        next: () => {
          processed++;
          if (processed === total) {
            this.selectedIndirizzi = [];
            this.handleSuccess('Raggruppato');
          }
        },
        error: err => this.handleError(err)
      });
    });
  }

  private handleSuccess(operation: string): void {
    this.message('success', operation, `${operation} ${this.itemTitle} completata`);
    this.resetForm();
  }

  private handleError(err: Error): void {
    if (err instanceof HttpErrorResponse && err.status === 409) {
      this.message('error', 'Errore', err.error);
    } else {
      this.message('error', 'Errore', 'Si è verificato un errore. Operazione non completata');
      this.resetForm();
    }
  }

  private message(sev: string, sum: string, det: string): void {
    this.messageService.add({
      key: 'toast',
      severity: sev,
      summary: sum,
      detail: det
    });
  }

  private buildPayload(base?: Indirizzo): Indirizzo {
    const value = this.form.value;
    return {
      ...(base ?? {}),
      nomeIndirizzo: value.name?.trim(),
      tipoSoggettoId: value.tipoSoggettoId,
      cognome: this.isPersonaFisica ? value.cognome?.trim() ?? null : null,
      codiceFiscale: value.codiceFiscale?.trim() ?? null,
      denominazioneAoo: this.isPai ? value.denominazioneAoo?.trim() ?? null : null,
      indirizziDigitaliRiferimento: this.isPae ? value.indirizziDigitaliRiferimento?.trim() ?? null : null
    } as Indirizzo;
  }

  private applyTipoSoggettoRules(resetValues = true): void {
    this.toggleValidators('cognome', this.isPersonaFisica);
    this.toggleValidators('codiceFiscale', false);
    this.toggleValidators('denominazioneAoo', this.isPai);
    this.toggleValidators('indirizziDigitaliRiferimento', this.isPae);

    if (resetValues) {
      if (!this.isPersonaFisica) {
        this.form.patchValue({ cognome: null }, { emitEvent: false });
      }
      if (!this.isPersonaGiuridica) {
        this.form.patchValue({ codiceFiscale: null }, { emitEvent: false });
      }
      if (!this.isPai) {
        this.form.patchValue({ denominazioneAoo: null }, { emitEvent: false });
      }
      if (!this.isPae) {
        this.form.patchValue({ indirizziDigitaliRiferimento: null }, { emitEvent: false });
      }
    }
  }

  private toggleValidators(controlName: string, required: boolean): void {
    const control = this.form.get(controlName);
    if (!control) {
      return;
    }
    control.setValidators(required ? [Validators.required] : []);
    control.updateValueAndValidity({ emitEvent: false });
  }

  public getTipoLabel(tipoId?: number | null): string {
    if (tipoId == null) {
      return '-';
    }

    return this.tipoLabelMap.get(tipoId) ?? '-';
  }

  public getNomeDisplay(item: Indirizzo): string {
    const rawName = item?.[this.nameProperty];
    const baseName =
      typeof rawName === 'string'
        ? rawName.trim()
        : rawName != null
          ? String(rawName)
          : '';

    if (item?.tipoSoggettoId === 1) {
      const cognome = item?.cognome?.trim();
      return [baseName, cognome].filter(Boolean).join(' ').trim();
    }

    return baseName;
  }

  public onTipoFilterChange(tipoId: number | null, table: Table): void {
    if (tipoId == null) {
      table.filter(null, 'tipoSoggettoId', 'equals');
      return;
    }

    table.filter(tipoId, 'tipoSoggettoId', 'equals');
  }
}
